/**
 * The structure a post shows readers and machines (2026-10-25).
 *
 * Held against the Torrum article canon, none of the 63 posts had a table of
 * contents, 1 of 486 section headings had an anchor, and 40 posts carried a
 * visible FAQ (an H3 question, then the answer) that only 2 also stated as
 * FAQPage data. The page (blog-post.ts, both sides) and the server's JSON-LD
 * read the same body, so the rules live here once.
 */
/** A post with this many sections gets a table of contents. */
export const TOC_MIN_SECTIONS = 6;
const H2 = /<h2(\s[^>]*)?>([\s\S]*?)<\/h2>/g;
/** An HTML fragment as text: tags dropped, the entities posts use decoded, spaces collapsed. */
export function htmlText(html) {
    return html
        .replace(/<[^>]+>/g, ' ')
        .replace(/&nbsp;/g, ' ')
        .replace(/&#x([0-9a-f]+);/gi, (_, h) => String.fromCodePoint(parseInt(h, 16)))
        .replace(/&#(\d+);/g, (_, n) => String.fromCodePoint(Number(n)))
        .replace(/&quot;/g, '"')
        .replace(/&lt;/g, '<')
        .replace(/&gt;/g, '>')
        .replace(/&amp;/g, '&')
        .replace(/\s+/g, ' ')
        .trim();
}
/**
 * A heading's anchor: its words in lower case, accents dropped, any script
 * kept (a Russian or Chinese heading reads as itself in the address).
 */
function slugOf(text) {
    return text
        .toLowerCase()
        .normalize('NFKD')
        .replace(/[̀-ͯ]/g, '')
        .replace(/[^\p{L}\p{N}]+/gu, '-')
        .slice(0, 64)
        .replace(/^-+|-+$/g, '');
}
/** Every H2 with an id (one it already has is kept), and the post's sections in order. */
export function anchorSections(body) {
    const sections = [];
    const used = new Set();
    const html = body.replace(H2, (whole, attrs, inner) => {
        const text = htmlText(inner);
        const own = /\sid="([^"]+)"/.exec(attrs ?? '')?.[1];
        let id = own ?? (slugOf(text) || `section-${sections.length + 1}`);
        for (let n = 2; !own && used.has(id); n++)
            id = `${slugOf(text) || 'section'}-${n}`;
        used.add(id);
        sections.push({ id, text });
        return own ? whole : `<h2 id="${id}"${attrs ?? ''}>${inner}</h2>`;
    });
    return { html, sections };
}
/** The FAQ heading in each language, as the posts write it. */
const FAQ_HEADINGS = new Set([
    'faq', 'faqs', 'frequently asked questions',
    'частые вопросы', 'часто задаваемые вопросы', 'вопросы и ответы',
    'preguntas frecuentes', 'perguntas frequentes',
    'sss', 'sık sorulan sorular', 'sıkça sorulan sorular',
    '常见问题',
]);
/**
 * The questions and answers of the post's FAQ section, as the page shows
 * them: each H3 is a question, what follows it up to the next H3 is the
 * answer. Two pairs at least, or none: one question is not a FAQ.
 */
export function bodyFaqPairs(body) {
    const heads = [...body.matchAll(H2)];
    const at = heads.findIndex((m) => FAQ_HEADINGS.has(htmlText(m[2] ?? '').toLowerCase().replace(/[:.]+$/, '')));
    if (at < 0)
        return [];
    const head = heads[at];
    const start = (head.index ?? 0) + head[0].length;
    const section = body.slice(start, heads[at + 1]?.index ?? body.length);
    const pairs = section.split(/<h3(?:\s[^>]*)?>/).slice(1).map((part) => {
        const [q = '', a = ''] = part.split('</h3>');
        return { q: htmlText(q), a: htmlText(a) };
    }).filter((p) => p.q && p.a);
    return pairs.length >= 2 ? pairs : [];
}
